import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/hooks';
import {
  useGetProjectBoardQuery,
  usePostProjectBoardEntryMutation,
  useUploadProjectBoardMediaMutation,
  useMarkBoardEntrySeenMutation,
  useToggleBoardEntryLikeMutation,
  useDeleteBoardEntryMutation,
} from '@/redux/internal-services/jobsApi';
import { subscribeEngagementSignal } from '@/components/SAM/samSignals';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Chip } from '@/components/UI/Chip';
import { IconButton } from '@/components/UI/IconButton';
import { SparklesIcon } from '@/components/UI/icons';
import { fieldClass } from '@/components/UI/fieldStyles';
import { useListTenantUsersQuery, type TenantUser } from '@/redux/internal-services/onboardingApi';
import { cn } from '@/utils/cn';
import { useSamSectionContext } from '@/hooks';

/** Post types the board knows (entry_subtype). Shown as each post's type tag; set by the quick chips. */
const TYPE_LABELS: Record<string, string> = {
  progress: 'Progress',
  work_note: 'Work note',
  material_issue: 'Material issue',
  customer_issue: 'Customer issue',
  crew_issue: 'Crew issue',
  delay: 'Delay',
  weather: 'Weather',
  completion: 'Completion',
};

/** A typed post with no quick chip is a work note. */
const DEFAULT_SUBTYPE = 'work_note';

const QUICK_ACTIONS = [
  { key: 'reachedSite', text: 'I reached site', subtype: 'progress' },
  { key: 'materialsArrived', text: 'Materials arrived', subtype: 'progress' },
  { key: 'workStarted', text: 'Work started', subtype: 'progress' },
  { key: 'taskCompleted', text: 'Task completed', subtype: 'progress' },
  { key: 'blockedOnSite', text: 'Blocked on site', subtype: 'crew_issue' },
];

function initials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  return parts.length === 1
    ? (parts[0][0] ?? '?').toUpperCase()
    : ((parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')).toUpperCase();
}

/** Shows `hint` in a small overlay on hover / keyboard focus; renders children unchanged without one. */
const HoverHint: React.FC<{ hint?: string | null; children: React.ReactNode }> = ({ hint, children }) => {
  if (!hint) return <>{children}</>;
  return (
    <span tabIndex={0} className="focus-ring group relative inline-flex items-center rounded-chip">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-0 z-dropdown mb-1 whitespace-nowrap rounded-chip border border-ui-border bg-ui-raised px-2 py-1 text-caption text-ui-text2 opacity-0 shadow-pop transition-opacity group-hover:opacity-100 group-focus:opacity-100"
      >
        {hint}
      </span>
    </span>
  );
};

function postedAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Render body text, wrapping @Name tokens that appear in mentionedNames in a highlight span. */
function renderBody(body: string, mentionNames: Set<string>): React.ReactNode {
  if (mentionNames.size === 0) return body;
  const parts = body.split(/(@\S+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@') && mentionNames.has(part.slice(1))) {
      return (
        <span key={i} className="font-semibold text-accent-text">
          {part}
        </span>
      );
    }
    return part;
  });
}

// ── @mention state ─────────────────────────────────────────────────────────

interface MentionState {
  active: boolean;
  triggerIndex: number; // index of the @ char in body
  query: string;        // text after @
}

const MENTION_NONE: MentionState = { active: false, triggerIndex: -1, query: '' };

// ── MentionDropdown ────────────────────────────────────────────────────────

interface MentionDropdownProps {
  users: TenantUser[];
  isFetching: boolean;
  activeIndex: number;
  onSelect: (user: TenantUser) => void;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
}

const MentionDropdown: React.FC<MentionDropdownProps> = ({ users, isFetching, activeIndex, onSelect, dropdownRef }) => {
  const { t } = useTranslation();
  return (
    <div
      ref={dropdownRef}
      className="absolute bottom-full left-0 z-dropdown mb-1 max-h-48 w-64 overflow-y-auto rounded-control border border-ui-border bg-ui-card shadow-pop"
    >
      {isFetching && users.length === 0 ? (
        <p className="px-3 py-2 text-caption text-ui-text3">{t('jobs.siteBoard.searchingPeople', 'Searching…')}</p>
      ) : users.length === 0 ? (
        <p className="px-3 py-2 text-caption text-ui-text3">{t('jobs.siteBoard.noPeopleFound', 'No users found')}</p>
      ) : (
        <ul>
          {users.map((u, idx) => (
            <li key={u.user_id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onSelect(u); }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                  idx === activeIndex ? 'bg-accent-soft text-accent-text' : 'text-ui-text hover:bg-ui-hover',
                )}
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-caption font-bold text-accent-text">
                  {initials(u.name)}
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-medium leading-tight">{u.name ?? u.email}</span>
                  {u.role_names.length > 0 && (
                    <span className="block truncate text-micro text-ui-text3">{u.role_names[0]}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── SiteBoardSection ───────────────────────────────────────────────────────

const SiteBoardSection: React.FC<{ engagementId: string }> = ({ engagementId }) => {
  const { t } = useTranslation();
  useSamSectionContext('site_board', { engagement_id: engagementId });
  const currentUserId = useAppSelector((s) => s.sessionState.userId) ?? '';
  const currentUserName = useAppSelector((s) => s.sessionState.userName);

  const { data, isLoading, isFetching, refetch } = useGetProjectBoardQuery(engagementId, {
    skip: !engagementId,
    pollingInterval: 15_000,
  });

  const [postEntry, { isLoading: isPosting }] = usePostProjectBoardEntryMutation();
  const [uploadMedia, { isLoading: isUploading }] = useUploadProjectBoardMediaMutation();
  const [markSeen] = useMarkBoardEntrySeenMutation();
  const [toggleLike] = useToggleBoardEntryLikeMutation();
  const [deleteEntry] = useDeleteBoardEntryMutation();
  const rootRef = useRef<HTMLDivElement>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [menuEntryId, setMenuEntryId] = useState<string | null>(null);

  // SAM's get_site_board / Site Board writes: bring the board (and a named post) into view, or refetch.
  useEffect(() => subscribeEngagementSignal('open_site_board', engagementId, (entryId) => {
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (entryId) setHighlightId(entryId);
  }), [engagementId]);
  useEffect(() => subscribeEngagementSignal('refresh_site_board', engagementId, () => { void refetch(); }), [engagementId, refetch]);

  const [selectedSubtype, setSelectedSubtype] = useState(DEFAULT_SUBTYPE);
  const [body, setBody] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  const [error, setError] = useState('');

  // Tracked mention user IDs collected during composition
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);

  // @mention picker state
  const [mention, setMention] = useState<MentionState>(MENTION_NONE);
  const [mentionActiveIdx, setMentionActiveIdx] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const markedSeen = useRef(new Set<string>());

  // Fetch matching users only while the picker is open
  const { data: usersData, isFetching: isFetchingUsers } = useListTenantUsersQuery(
    { search: mention.query.trim() || undefined, page_size: 8 },
    { skip: !mention.active },
  );
  const mentionUsers: TenantUser[] = usersData?.data ?? [];

  // Reset active item when results change
  useEffect(() => { setMentionActiveIdx(0); }, [mentionUsers.length]);

  // ── @mention detection ───────────────────────────────────────────────────

  const handleBodyChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setBody(val);
    // Clearing the box drops the type a quick chip set, so the next typed post is a work note again.
    if (!val.trim()) setSelectedSubtype(DEFAULT_SUBTYPE);

    const cursor = e.target.selectionStart ?? val.length;
    // Find the last @ before the cursor (not preceded by a non-space)
    const textBeforeCursor = val.slice(0, cursor);
    const match = textBeforeCursor.match(/(^|\s)@(\S*)$/);
    if (match) {
      const triggerIndex = textBeforeCursor.lastIndexOf('@');
      setMention({ active: true, triggerIndex, query: match[2] });
    } else {
      setMention(MENTION_NONE);
    }
  }, []);

  const insertMention = useCallback((user: TenantUser) => {
    const displayName = (user.name ?? user.email ?? 'unknown').replace(/\s+/g, '_');
    const before = body.slice(0, mention.triggerIndex);
    const after = body.slice(mention.triggerIndex + 1 + mention.query.length);
    const newBody = `${before}@${displayName}${after.startsWith(' ') ? '' : ' '}${after}`;
    setBody(newBody);
    setMention(MENTION_NONE);
    setMentionedUserIds((prev) => prev.includes(user.user_id) ? prev : [...prev, user.user_id]);
    // Restore focus and move cursor after the inserted token
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        const pos = before.length + displayName.length + 2; // @Name + space
        el.focus();
        el.setSelectionRange(pos, pos);
      }
    });
  }, [body, mention]);

  // Keyboard navigation within the dropdown
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention.active && mentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionActiveIdx((i) => (i + 1) % mentionUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionActiveIdx((i) => (i - 1 + mentionUsers.length) % mentionUsers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const user = mentionUsers[mentionActiveIdx];
        if (user) { e.preventDefault(); insertMention(user); return; }
      }
      if (e.key === 'Escape') {
        setMention(MENTION_NONE);
        return;
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      void handlePost();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mention, mentionUsers, mentionActiveIdx, insertMention]);

  // Scroll selected item into view
  useLayoutEffect(() => {
    const dd = dropdownRef.current;
    if (!dd) return;
    const item = dd.querySelectorAll('li')[mentionActiveIdx] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [mentionActiveIdx]);

  // ── files ────────────────────────────────────────────────────────────────

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []).slice(0, 5);
    if (!selected.length) return;
    setPendingFiles((prev) => [...prev, ...selected].slice(0, 5));
    selected.forEach((f) => {
      const reader = new FileReader();
      reader.onload = (ev) => setPendingPreviews((prev) => [...prev, ev.target?.result as string].slice(0, 5));
      reader.readAsDataURL(f);
    });
    e.target.value = '';
  };

  const removePendingFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const entries = data?.entries ?? [];

  // Mark unseen entries
  useEffect(() => {
    entries.forEach((entry) => {
      const seenBy = entry.seen_by ?? [];
      if (!markedSeen.current.has(entry.entry_id) && !seenBy.includes(currentUserId)) {
        markedSeen.current.add(entry.entry_id);
        void markSeen({ engagementId, entryId: entry.entry_id });
      }
    });
  }, [entries, currentUserId, engagementId, markSeen]);

  // ── post ─────────────────────────────────────────────────────────────────

  const handlePost = async () => {
    if (!body.trim() && pendingFiles.length === 0) return;
    setError('');
    try {
      let mediaUrls: string[] = [];
      if (pendingFiles.length > 0) {
        const result = await uploadMedia({ engagementId, files: pendingFiles }).unwrap();
        mediaUrls = result.urls;
      }
      const entryType = mediaUrls.length > 0 && !body.trim() ? 'photo' : 'note';
      await postEntry({
        engagementId,
        entry_type: entryType,
        entry_subtype: selectedSubtype,
        body: body.trim() || '📷 Photo',
        media_urls: mediaUrls,
        mention_user_ids: mentionedUserIds,
      }).unwrap();
      setBody('');
      setSelectedSubtype(DEFAULT_SUBTYPE);
      setPendingFiles([]);
      setPendingPreviews([]);
      setMentionedUserIds([]);
      setMention(MENTION_NONE);
    } catch (err) {
      setError((err as { data?: { message?: string } })?.data?.message ?? t('jobs.siteBoard.postFailed', 'Could not post.'));
    }
  };

  const removePost = (entryId: string) => {
    setMenuEntryId(null);
    if (!window.confirm(t('jobs.siteBoard.removeConfirm', 'Remove this post?'))) return;
    void deleteEntry({ engagementId, entryId }).unwrap()
      .catch((err) => setError((err as { data?: { detail?: string } })?.data?.detail ?? t('jobs.siteBoard.removeFailed', 'Could not remove the post.')));
  };

  const typeLabel = (subtype: string | null | undefined) =>
    t(`jobs.siteBoard.type.${subtype ?? DEFAULT_SUBTYPE}`, TYPE_LABELS[subtype ?? ''] ?? TYPE_LABELS[DEFAULT_SUBTYPE]);

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div ref={rootRef} className="overflow-hidden rounded-card border border-ui-border bg-ui-subtle">
      {/* header */}
      <div className="flex items-center gap-2.5 border-b border-ui-border bg-ui-card px-3.5 py-3">
        <div className="min-w-0 flex-1">
          <p className="eyebrow text-ui-text3">{t('jobs.siteBoard.title', 'Site Board')}</p>
          <p className="mt-0.5 text-caption text-ui-text3">
            {t('jobs.siteBoard.subtitle', 'Everything happening on this job, newest first · {{count}} posts', { count: entries.length })}
          </p>
        </div>
        {isFetching && !isLoading && <span className="text-micro text-ui-text3">{t('common.refreshing', 'Refreshing…')}</span>}
        <Badge variant="success" className="shrink-0">{t('jobs.siteBoard.live', 'Live')}</Badge>
      </div>

      {/* composer */}
      <div className="flex flex-col gap-2.5 border-b border-ui-border bg-ui-card px-3.5 py-3">
        <div className="flex items-start gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-caption font-bold text-accent-text">
            {initials(currentUserName)}
          </span>
          <div className="relative min-w-0 flex-1">
            {/* @mention dropdown — floats above the textarea */}
            {mention.active && (
              <MentionDropdown
                users={mentionUsers}
                isFetching={isFetchingUsers}
                activeIndex={mentionActiveIdx}
                onSelect={insertMention}
                dropdownRef={dropdownRef}
              />
            )}
            <textarea
              ref={textareaRef}
              value={body}
              rows={2}
              onChange={handleBodyChange}
              onKeyDown={handleKeyDown}
              placeholder={t('jobs.siteBoard.placeholder', 'Share an update with the job team… type @ to tag someone')}
              className={cn(fieldClass({ size: 'sm', shape: 'box' }), 'resize-y py-2.5 leading-relaxed')}
            />
          </div>
        </div>

        {/* quick updates — each sets the post's type */}
        <div className="no-scrollbar flex gap-1.5 overflow-x-auto pl-[42px]">
          {QUICK_ACTIONS.map((qa) => {
            const text = t(`jobs.siteBoard.quick.${qa.key}`, qa.text);
            return (
              <Chip
                key={qa.key}
                selected={body === text}
                onClick={() => { setSelectedSubtype(qa.subtype); setBody(text); textareaRef.current?.focus(); }}
                className="shrink-0 whitespace-nowrap"
              >
                {text}
              </Chip>
            );
          })}
        </div>

        {/* image previews */}
        {pendingPreviews.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pl-[42px]">
            {pendingPreviews.map((src, idx) => (
              <div key={idx} className="relative">
                <img src={src} alt="" className="h-16 w-16 rounded-chip border border-ui-border object-cover" />
                <IconButton
                  size="xs"
                  label={t('jobs.siteBoard.removePhoto', 'Remove photo')}
                  onClick={() => removePendingFile(idx)}
                  className="absolute -right-2 -top-2 bg-ui-card shadow-card"
                >
                  ×
                </IconButton>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 pl-[42px]">
          <Button
            variant="outline"
            size="sm"
            disabled={pendingFiles.length >= 5}
            onClick={() => fileInputRef.current?.click()}
          >
            ▣ {t('jobs.siteBoard.photo', 'Photo')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <div className="flex-1" />
          <Button
            variant="accent"
            size="sm"
            loading={isPosting || isUploading}
            disabled={isPosting || isUploading || (!body.trim() && pendingFiles.length === 0)}
            onClick={() => void handlePost()}
          >
            {t('jobs.siteBoard.post', 'Post')}
          </Button>
        </div>
        {error && <p className="pl-[42px] text-caption text-danger-text">{error}</p>}
      </div>

      {/* feed — newest first */}
      {isLoading ? (
        <p className="px-3.5 py-3 text-caption text-ui-text3">{t('common.loading', 'Loading…')}</p>
      ) : entries.length === 0 ? (
        <p className="px-3.5 py-3 text-caption text-ui-text3">
          {t('jobs.siteBoard.empty', 'No updates yet — be the first to post!')}
        </p>
      ) : (
        <ul className="flex max-h-[480px] flex-col gap-2.5 overflow-y-auto px-3.5 py-3">
          {entries.map((entry) => {
            const likesList = entry.likes ?? [];
            const seenByList = entry.seen_by ?? [];
            const liked = likesList.includes(currentUserId);
            const isSystem = entry.entry_type === 'status_event';
            const photos = entry.media_urls ?? [];
            const canRemove = entry.author_user_id === currentUserId && entry.source !== 'system';
            // Build the set of names mentioned in this entry for highlight rendering
            const entryMentionNames: Set<string> = new Set();
            if ((entry.mention_user_ids?.length ?? 0) > 0) {
              // Extract @Token names from the body itself — avoids needing a user lookup per entry
              const matches = entry.body.match(/@(\S+)/g);
              matches?.forEach((m) => entryMentionNames.add(m.slice(1)));
            }
            return (
              <li
                key={entry.entry_id}
                className={cn(
                  // No overflow-hidden: it would clip the "•••" menu.
                  'rounded-card border bg-ui-card',
                  highlightId === entry.entry_id ? 'border-accent ring-2 ring-ui-ring' : 'border-ui-border',
                )}
              >
                {/* author row */}
                <div className="flex items-center gap-2.5 px-3 py-2.5">
                  <span className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-caption font-bold',
                    isSystem ? 'bg-steel/15 text-steel-text' : 'bg-accent-soft text-accent-text',
                  )}>
                    {isSystem ? '◎' : initials(entry.author_name)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <HoverHint hint={isSystem ? null : entry.author_role}>
                        <span className="text-caption font-semibold text-ui-text">
                          {entry.author_name ?? t('jobs.siteBoard.system', 'System')}
                        </span>
                      </HoverHint>
                      {isSystem && (
                        <Badge variant="neutral" className="px-1.5 text-micro uppercase tracking-wide">
                          {t('jobs.siteBoard.system', 'System')}
                        </Badge>
                      )}
                      {!isSystem && (
                        <Badge variant="accent" className="px-1.5 text-micro">{typeLabel(entry.entry_subtype)}</Badge>
                      )}
                      {entry.source === 'sam' && (
                        <HoverHint hint={t('jobs.siteBoard.viaSam', 'via SAM')}>
                          <SparklesIcon size={16} className="text-accent-text" />
                        </HoverHint>
                      )}
                    </div>
                    <p className="mt-0.5 text-micro text-ui-text3">{postedAt(entry.created_on)}</p>
                  </div>
                  {canRemove && (
                    <div className="relative shrink-0">
                      <IconButton
                        size="xs"
                        label={t('jobs.siteBoard.postActions', 'Post actions')}
                        active={menuEntryId === entry.entry_id}
                        onClick={() => setMenuEntryId((id) => (id === entry.entry_id ? null : entry.entry_id))}
                      >
                        •••
                      </IconButton>
                      {menuEntryId === entry.entry_id && (
                        <div className="absolute right-0 top-full z-dropdown mt-1 rounded-control border border-ui-border bg-ui-card p-1 shadow-pop">
                          <Button variant="destructive-ghost" size="xs" onClick={() => removePost(entry.entry_id)}>
                            {t('jobs.siteBoard.remove', 'Remove')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* body — skip the auto-placeholder we inject when posting photo-only */}
                {entry.body && entry.body !== '📷 Photo' && (
                  <p className="px-3 pb-2.5 text-body leading-relaxed text-ui-text text-pretty">
                    {renderBody(entry.body, entryMentionNames)}
                  </p>
                )}

                {/* attached photos */}
                {photos.length > 0 && (
                  <>
                    <div className="flex flex-wrap gap-1.5 px-3 pb-2.5">
                      {photos.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={url}
                            alt=""
                            className="h-20 w-20 rounded-chip border border-ui-border object-cover transition-opacity hover:opacity-90"
                          />
                        </a>
                      ))}
                    </div>
                    <p className="border-t border-ui-border bg-ui-subtle px-3 py-2 text-micro text-accent-text">
                      {t('jobs.siteBoard.photosAttached', '{{count}} photos attached', { count: photos.length })}
                    </p>
                  </>
                )}

                {/* footer */}
                <div className="flex items-center gap-4 border-t border-ui-border px-3 py-2">
                  <button
                    type="button"
                    aria-pressed={liked}
                    aria-label={liked ? t('jobs.siteBoard.unlike', 'Unlike') : t('jobs.siteBoard.like', 'Like')}
                    onClick={() => void toggleLike({ engagementId, entryId: entry.entry_id })}
                    className={cn(
                      'focus-ring flex items-center gap-1.5 rounded-full text-caption font-semibold transition-colors',
                      liked ? 'text-danger-text' : 'text-ui-text2 hover:text-danger-text',
                    )}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill={liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                    </svg>
                    {likesList.length}
                  </button>
                  <div className="flex-1" />
                  {seenByList.length > 0 && (
                    <span className="text-micro font-semibold text-success-text">
                      {t('jobs.siteBoard.seenBy', 'Seen by {{count}}', { count: seenByList.length })}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

    </div>
  );
};

export default SiteBoardSection;
